import type { CatalogMediaPurpose } from '../../../catalog-media/catalogMediaApi';

export const LOCAL_DEMO_MEDIA_DB_NAME = 'cpq-local-database-demo-media-v1';
const STORE_NAME = 'objects';

export interface LocalDemoMediaRecord {
  objectId: string;
  fileName: string;
  mediaType: string;
  byteSize: number;
  sha256: string;
  purpose: CatalogMediaPurpose;
  blob: Blob;
  status: 'uploaded' | 'clean';
}

type StoredLocalDemoMediaRecord = Omit<LocalDemoMediaRecord, 'blob'> & {
  data: ArrayBuffer;
};

let databasePromise: Promise<IDBDatabase> | undefined;
const objectUrls = new Map<string, string>();

function database(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined')
    return Promise.reject(new Error('浏览器不支持本地演示图片存储'));
  databasePromise ??= new Promise((resolve, reject) => {
    const request = indexedDB.open(LOCAL_DEMO_MEDIA_DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME))
        request.result.createObjectStore(STORE_NAME, { keyPath: 'objectId' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      databasePromise = undefined;
      reject(request.error ?? new Error('无法打开本地演示图片存储'));
    };
    request.onblocked = () => {
      databasePromise = undefined;
      reject(new Error('本地演示图片存储正被其他页面占用，请刷新后重试'));
    };
  });
  return databasePromise;
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error('本地图片读写失败'));
  });
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () =>
      reject(transaction.error ?? new Error('本地图片事务已取消'));
    transaction.onerror = () =>
      reject(transaction.error ?? new Error('本地图片事务失败'));
  });
}

async function put(record: LocalDemoMediaRecord): Promise<void> {
  const { blob, ...metadata } = record;
  const stored: StoredLocalDemoMediaRecord = {
    ...metadata,
    data: await blob.arrayBuffer(),
  };
  const db = await database();
  const transaction = db.transaction(STORE_NAME, 'readwrite');
  const completed = transactionComplete(transaction);
  const written = requestResult(
    transaction.objectStore(STORE_NAME).put(stored),
  );
  await Promise.all([written, completed]);
}

export async function saveLocalDemoMediaObject(
  record: LocalDemoMediaRecord,
): Promise<string> {
  await put(record);
  const existing = objectUrls.get(record.objectId);
  if (existing) URL.revokeObjectURL(existing);
  const url = URL.createObjectURL(record.blob);
  objectUrls.set(record.objectId, url);
  return url;
}

export async function getLocalDemoMediaObject(
  objectId: string,
): Promise<LocalDemoMediaRecord | null> {
  const db = await database();
  const transaction = db.transaction(STORE_NAME, 'readonly');
  const stored = await requestResult<StoredLocalDemoMediaRecord | undefined>(
    transaction.objectStore(STORE_NAME).get(objectId),
  );
  if (!stored) return null;
  const { data, ...metadata } = stored;
  return { ...metadata, blob: new Blob([data], { type: stored.mediaType }) };
}

export async function getLocalDemoMediaPreview(
  objectId: string,
): Promise<string | null> {
  const cached = objectUrls.get(objectId);
  if (cached) return cached;
  const record = await getLocalDemoMediaObject(objectId);
  if (record?.status !== 'clean') return null;
  const url = URL.createObjectURL(record.blob);
  objectUrls.set(objectId, url);
  return url;
}

/** Replace only catalog-media preview URLs; all business facts remain untouched. */
export async function hydrateLocalDemoMedia<T>(value: T): Promise<T> {
  if (Array.isArray(value))
    return Promise.all(
      value.map((item) => hydrateLocalDemoMedia(item)),
    ) as Promise<T>;
  if (value === null || typeof value !== 'object') return value;
  const source = value as Record<string, unknown>;
  const objectId = source.objectId ?? source.storedObjectId;
  const isMedia =
    typeof objectId === 'string' &&
    typeof source.mediaType === 'string' &&
    ('previewUrl' in source || 'thumbnailUrl' in source);
  if (isMedia) {
    const preview = await getLocalDemoMediaPreview(String(objectId));
    if (!preview) return value;
    return {
      ...source,
      previewUrl: preview,
      thumbnailUrl: preview,
    } as T;
  }
  const entries = await Promise.all(
    Object.entries(source).map(
      async ([key, item]) => [key, await hydrateLocalDemoMedia(item)] as const,
    ),
  );
  return Object.fromEntries(entries) as T;
}
